// src/pages/markets/components/PositionsTable.tsx
//
// Generic, context-agnostic positions table.
//
// Sort is driven entirely by clicking column headers — no chips, no toggles.
// Clicking a header that is not yet active switches to that sort (default dir).
// Clicking the active header toggles asc ↔ desc.
// Pagination uses MUI TablePagination (rows-per-page selector + page nav).

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Box,
    Chip,
    Menu,
    MenuItem,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
    IconButton,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { NEW_TAB_LINK_PROPS } from '../../../utils/newTab';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import type {
    TraderPosition,
    PositionCategory,
} from '../api/traderProfileTypes';
import { POSITION_CATEGORIES } from '../api/traderProfileTypes';
import { usePositions, POSITION_SORT_DEFAULT_DIR } from '../api/usePositions';
import type { PositionSort, SortDir } from '../api/usePositions';
import { usePositionTrades } from '../api/usePositionTrades';
import { TradesTable } from './TradesTable';

// ─── formatters ──────────────────────────────────────────────────────────────

function fmtUsd(n: number | null | undefined): string {
    if (n == null || !Number.isFinite(n)) return '—';
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
    return `$${n.toFixed(2)}`;
}

function fmtPnl(n: number | null | undefined): string {
    if (n == null || !Number.isFinite(n)) return '—';
    const s = fmtUsd(Math.abs(n));
    return n >= 0 ? `+${s}` : `−${s}`;
}

function fmtPct(p: number | null | undefined): string {
    if (p == null || !Number.isFinite(p)) return '—';
    return `${(p * 100).toFixed(1)}¢`;
}

function fmtNum(n: number | null | undefined): string {
    if (n == null || !Number.isFinite(n)) return '—';
    return n.toFixed(1);
}

function fmtDays(n: number | null | undefined): string {
    if (n == null || !Number.isFinite(n)) return '—';
    if (n < 1) return `${Math.round(n * 24)}h`;
    return `${n.toFixed(1)}d`;
}

function fmtDateTime(s: string | null | undefined): string {
    if (!s) return '—';
    const d = new Date(s);
    if (!isFinite(d.getTime())) return '—';

    const pad = (n: number) => n.toString().padStart(2, '0');

    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ─── column config ───────────────────────────────────────────────────────────

export interface PositionColumnCtx {
    onFilterByEvent?: (eventId: string, side?: 'YES' | 'NO') => void;
    traderAddress?: string;
}

export interface PositionColumn {
    id: string;
    label: string;
    tooltip?: string;
    align?: 'left' | 'right';
    // which API sort key this header controls; null = not sortable
    sortKey?: PositionSort | null;
    render: (p: TraderPosition, ctx: PositionColumnCtx) => React.ReactNode;
}

// ── cell sub-components ──

const CATEGORY_LABELS: Record<PositionCategory, string> = {
    politics: 'Politics',
    sports: 'Sports',
    crypto: 'Crypto',
    finance: 'Finance',
    culture: 'Culture',
    mentions: 'Mentions',
    weather: 'Weather',
    economics: 'Economics',
    tech: 'Tech',
    other: 'Other',
};

function CategoryBadge({ category }: { category: PositionCategory }) {
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-block',
                px: 0.6,
                py: 0.1,
                borderRadius: 0.75,
                fontSize: '10px',
                fontWeight: 600,
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                bgcolor: theme => alpha(theme.palette.text.secondary, 0.1),
            }}
        >
            {CATEGORY_LABELS[category]}
        </Box>
    );
}

function SideBadge({ side }: { side: 'YES' | 'NO' }) {
    const isYes = side === 'YES';
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-block',
                px: 0.6,
                py: 0.1,
                borderRadius: 0.75,
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.03em',
                color: isYes ? 'success.main' : 'error.main',
                bgcolor: theme =>
                    alpha(
                        isYes
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                        0.12
                    ),
            }}
        >
            {side}
        </Box>
    );
}

function MarketCell({
    pos,
    traderAddress,
}: {
    pos: TraderPosition;
    traderAddress?: string;
}) {
    const href = pos.event_slug
        ? `/markets/event/${pos.event_slug}?market=${pos.condition_id}` +
          (traderAddress ? `&traders=${encodeURIComponent(traderAddress)}` : '')
        : undefined;

    return (
        <Box sx={{ maxWidth: 450 }}>
            <Typography
                component={href ? Link : 'span'}
                {...(href ? { to: href, ...NEW_TAB_LINK_PROPS } : {})}
                sx={{
                    fontSize: '12px',
                    color: 'text.primary',
                    textDecoration: 'none',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    '&:hover': href ? { color: 'primary.main' } : undefined,
                }}
            >
                {pos.market_question || '—'}
            </Typography>
        </Box>
    );
}

function EventFilterCell({
    pos,
    ctx,
}: {
    pos: TraderPosition;
    ctx: PositionColumnCtx;
}) {
    if (!pos.event_id) {
        return (
            <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                —
            </Typography>
        );
    }
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Tooltip title="Filter table to this event" arrow placement="top">
                <IconButton
                    size="small"
                    onClick={() =>
                        ctx.onFilterByEvent?.(pos.event_id!, pos.side)
                    }
                    sx={{ p: 0.25, opacity: 0.6, '&:hover': { opacity: 1 } }}
                >
                    <FilterAltIcon sx={{ fontSize: 13 }} />
                </IconButton>
            </Tooltip>
        </Box>
    );
}

function PnlValue({ n }: { n: number | null | undefined }) {
    if (n == null || !Number.isFinite(n)) {
        return (
            <Typography
                sx={{
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    color: 'text.disabled',
                }}
            >
                —
            </Typography>
        );
    }
    return (
        <Typography
            sx={{
                fontSize: '12px',
                fontFamily: 'monospace',
                fontWeight: 600,
                color: n >= 0 ? 'success.main' : 'error.main',
            }}
        >
            {fmtPnl(n)}
        </Typography>
    );
}

function PnlCell({ pos }: { pos: TraderPosition }) {
    const total = pos.pnl;
    return (
        <Tooltip
            arrow
            placement="top"
            title={
                <Box sx={{ fontSize: '11px', lineHeight: 1.7 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                        }}
                    >
                        <span style={{ opacity: 0.7 }}>Realized</span>
                        <span>{fmtPnl(pos.realized_pnl)}</span>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                        }}
                    >
                        <span style={{ opacity: 0.7 }}>Unrealized</span>
                        <span>{fmtPnl(pos.unrealized_pnl)}</span>
                    </Box>
                </Box>
            }
        >
            <Typography
                sx={{
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    color: total >= 0 ? 'success.main' : 'error.main',
                }}
            >
                {fmtPnl(total)}
            </Typography>
        </Tooltip>
    );
}

function BuySellCell({ pos }: { pos: TraderPosition }) {
    const buyCount = pos.buy_count ?? 0;
    const sellCount = pos.sell_count ?? 0;
    return (
        <Tooltip
            arrow
            placement="top"
            title={
                <Box sx={{ fontSize: '11px', lineHeight: 1.7 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                        }}
                    >
                        <span style={{ opacity: 0.7 }}>Buy vol</span>
                        <span>{fmtUsd(pos.total_buy_quote)}</span>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                        }}
                    >
                        <span style={{ opacity: 0.7 }}>Sell vol</span>
                        <span>{fmtUsd(pos.total_sell_quote)}</span>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                        }}
                    >
                        <span style={{ opacity: 0.7 }}>Shares bought</span>
                        <span>{fmtNum(pos.shares_bought)}</span>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                        }}
                    >
                        <span style={{ opacity: 0.7 }}>Shares sold</span>
                        <span>{fmtNum(pos.shares_sold)}</span>
                    </Box>
                </Box>
            }
        >
            <Typography
                sx={{
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    whiteSpace: 'nowrap',
                }}
            >
                {buyCount} / {sellCount}
            </Typography>
        </Tooltip>
    );
}

function OutcomeCell({ pos }: { pos: TraderPosition }) {
    if (pos.won === true)
        return (
            <Typography
                sx={{
                    fontSize: '12px',
                    color: 'success.main',
                    fontWeight: 500,
                }}
            >
                Win
            </Typography>
        );
    if (pos.won === false)
        return (
            <Typography
                sx={{ fontSize: '12px', color: 'error.main', fontWeight: 500 }}
            >
                Loss
            </Typography>
        );
    if (pos.open_position != null)
        return (
            <Typography
                sx={{
                    fontSize: '12px',
                    color: 'warning.main',
                    fontWeight: 500,
                }}
            >
                Open
            </Typography>
        );
    return (
        <Typography
            sx={{
                fontSize: '12px',
                fontWeight: 500,
                opacity: 0.6,
                color: pos.pnl >= 0 ? 'success.main' : 'error.main',
            }}
        >
            Closed
        </Typography>
    );
}

// ── column definitions ──
//
// sortKey wiring:
//   B/S       → 'notable'       (absolute PnL magnitude — original default)
//   PnL       → 'total_pnl'     (signed realized + unrealized)
//   Value     → 'current_value' (open_position × price)
//   Open      → 'open_position' (net shares)
//   Last trade → 'recent'       (last_trade_at)

export const POSITION_COLUMNS: PositionColumn[] = [
    {
        id: 'event',
        label: '',
        tooltip: 'Filter by event',
        render: (p, ctx) => <EventFilterCell pos={p} ctx={ctx} />,
    },
    {
        id: 'market',
        label: 'Market',
        render: (p, ctx) => (
            <MarketCell pos={p} traderAddress={ctx.traderAddress} />
        ),
    },
    {
        id: 'side',
        label: 'Side',
        render: p => <SideBadge side={p.side} />,
    },
    {
        id: 'category',
        label: 'Category',
        tooltip: 'Filter by category',
        render: p => <CategoryBadge category={p.category} />,
    },
    {
        id: 'curValue',
        label: 'Value',
        align: 'right',
        sortKey: 'current_value',
        render: p => (
            <Tooltip
                arrow
                placement="top"
                title={
                    <Box sx={{ fontSize: '11px', lineHeight: 1.7 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 2,
                            }}
                        >
                            <span style={{ opacity: 0.7 }}>Market value</span>
                            <span>{fmtUsd(p.current_value)}</span>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 2,
                            }}
                        >
                            <span style={{ opacity: 0.7 }}>Cost basis</span>
                            <span>{fmtUsd(p.cost_basis)}</span>
                        </Box>
                    </Box>
                }
            >
                <span>{fmtUsd(p.current_value)}</span>
            </Tooltip>
        ),
    },
    {
        id: 'openShares',
        label: 'Open',
        align: 'right',
        sortKey: 'open_position',
        tooltip: 'Net shares still held — click to sort',
        render: p => (p.open_position != null ? fmtNum(p.open_position) : '—'),
    },
    {
        id: 'entry',
        label: 'Avg entry',
        align: 'right',
        tooltip: 'Average price paid (buy) for this side',
        render: p => fmtPct(p.entry_price),
    },
    {
        id: 'exit',
        label: 'Avg exit',
        align: 'right',
        tooltip: 'Average price received (sell) for this side',
        render: p => fmtPct(p.avg_exit_price),
    },
    {
        id: 'current',
        label: 'Current',
        align: 'right',
        tooltip: 'Live market price of this side',
        render: p => fmtPct(p.current_price),
    },
    {
        id: 'pnl',
        label: 'PnL',
        align: 'right',
        sortKey: 'total_pnl',
        render: p => <PnlCell pos={p} />,
    },
    {
        id: 'realizedPnl',
        label: 'Realized',
        sortKey: 'realized_pnl',
        align: 'right',
        tooltip: 'PnL locked in from closed / partial sells on this position',
        render: p => <PnlValue n={p.realized_pnl} />,
    },
    {
        id: 'unrealizedPnl',
        label: 'Unrealized',
        sortKey: 'unrealized_pnl',
        align: 'right',
        tooltip: 'Mark-to-market PnL on shares still held',
        render: p => <PnlValue n={p.unrealized_pnl} />,
    },
    {
        id: 'trades',
        label: 'B / S',
        align: 'right',
        tooltip:
            'Buy / Sell trade count — click to sort by notable (|PnL|); hover for volume detail',
        render: p => <BuySellCell pos={p} />,
    },
    {
        id: 'hold',
        label: 'Hold',
        align: 'right',
        render: p => fmtDays(p.hold_days),
    },
    {
        id: 'lastTrade',
        label: 'Last trade',
        align: 'right',
        sortKey: 'recent',
        tooltip: 'Time of most recent fill — click to sort',
        render: p => (
            <Typography
                sx={{
                    fontSize: '11px',
                    fontFamily: 'monospace',
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                }}
            >
                {fmtDateTime(p.last_trade_at)}
            </Typography>
        ),
    },
    {
        id: 'outcome',
        label: 'Out',
        render: p => <OutcomeCell pos={p} />,
    },
];

// ─── lazy trades panel ───────────────────────────────────────────────────────

function PositionTradesPanel({
    address,
    pos,
    colSpan,
}: {
    address: string;
    pos: TraderPosition;
    colSpan: number;
}) {
    // No outcomeIndex filter — pull all fills for the market, both sides.
    const { trades, loading, error } = usePositionTrades(
        address,
        pos.condition_id,
        undefined,
        true
    );
    return (
        <TableRow>
            <TableCell colSpan={colSpan} sx={{ p: 0, border: 0 }}>
                {loading ? (
                    <Box sx={{ p: 1.5 }}>
                        {[...Array(3)].map((_, i) => (
                            <Skeleton key={i} height={20} sx={{ mb: 0.5 }} />
                        ))}
                    </Box>
                ) : error ? (
                    <Typography
                        sx={{ p: 1.5, fontSize: '11px', color: 'error.main' }}
                    >
                        Couldn't load trades for this position.
                    </Typography>
                ) : (
                    <TradesTable trades={trades} positionSide={pos.side} />
                )}
            </TableCell>
        </TableRow>
    );
}

// ─── position row ─────────────────────────────────────────────────────────────

interface PositionRowProps {
    address: string;
    pos: TraderPosition;
    columns: PositionColumn[];
    ctx: PositionColumnCtx;
    expandable: boolean;
}

const PositionRow = React.memo(function PositionRow({
    address,
    pos,
    columns,
    ctx,
    expandable,
}: PositionRowProps) {
    const [expanded, setExpanded] = useState(false);
    const colSpan = columns.length + (expandable ? 1 : 0);

    return (
        <>
            <TableRow
                hover
                sx={{ '& td': { borderBottom: expanded ? 0 : undefined } }}
            >
                {expandable && (
                    <TableCell sx={{ py: 0.5, width: 28, pr: 0 }}>
                        <IconButton
                            size="small"
                            onClick={() => setExpanded(e => !e)}
                            sx={{ p: 0.25 }}
                            aria-label={expanded ? 'Collapse' : 'Expand'}
                        >
                            {expanded ? (
                                <ExpandMoreIcon sx={{ fontSize: 16 }} />
                            ) : (
                                <ChevronRightIcon sx={{ fontSize: 16 }} />
                            )}
                        </IconButton>
                    </TableCell>
                )}
                {columns.map(col => (
                    <TableCell
                        key={col.id}
                        align={col.align ?? 'left'}
                        sx={{
                            py: 0.75,
                            fontSize: '12px',
                            fontFamily:
                                col.align === 'right' ? 'monospace' : undefined,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {col.render(pos, ctx)}
                    </TableCell>
                ))}
            </TableRow>
            {expandable && expanded && (
                <PositionTradesPanel
                    address={address}
                    pos={pos}
                    colSpan={colSpan}
                />
            )}
        </>
    );
});

// ─── sortable column header ───────────────────────────────────────────────────

function SortIndicator({ dir }: { dir: SortDir }) {
    return dir === 'asc' ? (
        <ArrowUpwardIcon sx={{ fontSize: 11, ml: 0.4, opacity: 0.7 }} />
    ) : (
        <ArrowDownwardIcon sx={{ fontSize: 11, ml: 0.4, opacity: 0.7 }} />
    );
}

// ─── table shell ─────────────────────────────────────────────────────────────

export interface PositionsTableProps {
    address: string | undefined;
    columns?: PositionColumn[];
    /** Initial rows-per-page; user can change via the pagination control */
    pageSize?: number;
    /** Initial sort key */
    initialSort?: PositionSort;
    eventId?: string | null;
    side?: 'YES' | 'NO' | null;
    category?: PositionCategory | null;
    /** Restricts the category dropdown to these categories; omit to show all */
    availableCategories?: PositionCategory[];
    /** ISO datetime — only positions held inside this window; controlled by the parent */
    heldFrom?: string | null;
    heldTo?: string | null;
    expandable?: boolean;
}

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

export function PositionsTable({
    address,
    columns = POSITION_COLUMNS,
    pageSize: initialPageSize = 25,
    initialSort = 'recent',
    eventId: eventIdProp = null,
    side: sideProp = null,
    category: categoryProp = null,
    availableCategories,
    heldFrom = null,
    heldTo = null,
    expandable = true,
}: PositionsTableProps) {
    // ── sort state ──
    const [sort, setSort] = useState<PositionSort>(initialSort);
    const [sortDir, setSortDir] = useState<SortDir>(
        POSITION_SORT_DEFAULT_DIR[initialSort]
    );

    // ── pagination state ──
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(initialPageSize);

    // ── filter state ──
    const [eventId, setEventId] = useState<string | null>(eventIdProp);
    const [side, setSide] = useState<'YES' | 'NO' | null>(sideProp);
    const [category, setCategory] = useState<PositionCategory | null>(
        categoryProp
    );

    useEffect(() => {
        setPage(0);
    }, [heldFrom, heldTo]);

    // Reset to page 0 whenever sort / filter changes
    const handleSortChange = useCallback(
        (key: PositionSort) => {
            if (key === sort) {
                // toggle direction on the active column
                setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
            } else {
                setSort(key);
                setSortDir(POSITION_SORT_DEFAULT_DIR[key]);
            }
            setPage(0);
        },
        [sort]
    );

    const onFilterByEvent = useCallback((evId: string, s?: 'YES' | 'NO') => {
        setEventId(evId);
        setSide(s ?? null);
        setPage(0);
    }, []);

    const clearFilter = useCallback(() => {
        setEventId(eventIdProp);
        setSide(sideProp);
        setPage(0);
    }, [eventIdProp, sideProp]);

    // ── category header dropdown ──
    const [categoryMenuAnchor, setCategoryMenuAnchor] =
        useState<HTMLElement | null>(null);

    const openCategoryMenu = useCallback((e: React.MouseEvent<HTMLElement>) => {
        setCategoryMenuAnchor(e.currentTarget);
    }, []);

    const closeCategoryMenu = useCallback(() => {
        setCategoryMenuAnchor(null);
    }, []);

    const handleCategorySelect = useCallback((c: PositionCategory | null) => {
        setCategory(c);
        setPage(0);
        setCategoryMenuAnchor(null);
    }, []);

    const handlePageChange = useCallback((_: unknown, newPage: number) => {
        setPage(newPage);
    }, []);

    const handleRowsPerPageChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
        },
        []
    );

    const { positions, loading, error, total } = usePositions(address, {
        sort,
        sortDir,
        page,
        pageSize,
        eventId,
        side,
        category,
        heldFrom,
        heldTo,
    });

    const ctx = useMemo<PositionColumnCtx>(
        () => ({ onFilterByEvent, traderAddress: address }),
        [onFilterByEvent, address]
    );

    const effectiveColumns = useMemo(() => {
        if (eventIdProp) return columns.filter(c => c.id !== 'event');
        return columns;
    }, [columns, eventIdProp]);

    const filterActive = eventId !== eventIdProp || side !== sideProp;
    const totalColSpan = effectiveColumns.length + (expandable ? 1 : 0);

    const categoryOptions = useMemo(() => {
        if (!availableCategories) return POSITION_CATEGORIES;
        const allowed = new Set(availableCategories);
        return POSITION_CATEGORIES.filter(c => allowed.has(c));
    }, [availableCategories]);

    return (
        <Box>
            {/* active-filter chip — only shown when an event filter is applied */}
            {filterActive && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mt: 1.5,
                        mb: 1,
                        ml: 1,
                    }}
                >
                    <Chip
                        size="small"
                        onDelete={clearFilter}
                        deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                        label={`Event filter${side ? ` · ${side}` : ''}`}
                        sx={{ height: 22, fontSize: '11px' }}
                    />
                </Box>
            )}

            <Menu
                anchorEl={categoryMenuAnchor}
                open={Boolean(categoryMenuAnchor)}
                onClose={closeCategoryMenu}
            >
                <MenuItem
                    selected={category === null}
                    onClick={() => handleCategorySelect(null)}
                    sx={{ fontSize: '12px' }}
                >
                    All categories
                </MenuItem>
                {categoryOptions.map(c => (
                    <MenuItem
                        key={c}
                        selected={category === c}
                        onClick={() => handleCategorySelect(c)}
                        sx={{ fontSize: '12px' }}
                    >
                        {CATEGORY_LABELS[c]}
                    </MenuItem>
                ))}
            </Menu>

            {/* states */}
            <Box sx={{ overflowX: 'auto' }}>
                <Table
                    size="small"
                    sx={{
                        minWidth: 900,
                        '& .MuiTableCell-root': {
                            px: 0.5,
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            {expandable && (
                                <TableCell sx={{ width: 28, p: 0 }} />
                            )}
                            {effectiveColumns.map(col => {
                                const isCategoryCol = col.id === 'category';
                                const sortable = !!col.sortKey;
                                const active = sortable && col.sortKey === sort;
                                const categoryFilterActive =
                                    isCategoryCol && category !== null;

                                const header = (
                                    <TableCell
                                        key={col.id}
                                        align={col.align ?? 'left'}
                                        onClick={
                                            isCategoryCol
                                                ? openCategoryMenu
                                                : sortable
                                                  ? () =>
                                                        handleSortChange(
                                                            col.sortKey!
                                                        )
                                                  : undefined
                                        }
                                        sx={{
                                            fontSize: '10.5px',
                                            fontWeight: 600,
                                            letterSpacing: '0.04em',
                                            textTransform: 'uppercase',
                                            color:
                                                active || categoryFilterActive
                                                    ? 'primary.main'
                                                    : 'text.secondary',
                                            cursor:
                                                isCategoryCol || sortable
                                                    ? 'pointer'
                                                    : 'default',
                                            whiteSpace: 'nowrap',
                                            userSelect: 'none',
                                        }}
                                    >
                                        <Box
                                            component="span"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            {col.label}
                                            {active && (
                                                <SortIndicator dir={sortDir} />
                                            )}
                                            {isCategoryCol && (
                                                <ArrowDropDownIcon
                                                    sx={{
                                                        fontSize: 16,
                                                        ml: 0.1,
                                                        opacity: 0.7,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                );

                                return col.tooltip ? (
                                    <Tooltip
                                        key={col.id}
                                        title={col.tooltip}
                                        arrow
                                        placement="top"
                                    >
                                        {header}
                                    </Tooltip>
                                ) : (
                                    header
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            [...Array(pageSize)].map((_, i) => (
                                <TableRow key={i}>
                                    <TableCell
                                        colSpan={totalColSpan}
                                        sx={{ py: 0.5 }}
                                    >
                                        <Skeleton height={26} />
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : error ? (
                            <TableRow>
                                <TableCell
                                    colSpan={totalColSpan}
                                    sx={{
                                        p: 1.5,
                                        fontSize: '12px',
                                        color: 'error.main',
                                        border: 0,
                                    }}
                                >
                                    Couldn't load positions. Try again.
                                </TableCell>
                            </TableRow>
                        ) : positions.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={totalColSpan}
                                    sx={{
                                        p: 1.5,
                                        fontSize: '12px',
                                        color: 'text.disabled',
                                        border: 0,
                                    }}
                                >
                                    No positions to show.
                                </TableCell>
                            </TableRow>
                        ) : (
                            positions.map(pos => (
                                <PositionRow
                                    key={`${pos.condition_id}-${pos.side}`}
                                    address={address!}
                                    pos={pos}
                                    columns={effectiveColumns}
                                    ctx={ctx}
                                    expandable={expandable}
                                />
                            ))
                        )}
                    </TableBody>
                </Table>

                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                    sx={{
                        borderTop: 1,
                        borderColor: 'divider',
                        '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows':
                            {
                                fontSize: '11px',
                            },
                    }}
                />
            </Box>
        </Box>
    );
}
